"use client";
import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";
import SleekButton from "../ui/NavButtons";
import { ThemeToggle } from "../ui/ThemeToggle";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Platform", href: "/platform" },
  { label: "Pricing", href: "/pricing" },
  { label: "Company", href: "/company" },
  { label: "Contact", href: "/contact" },
];

const NavItem: React.FC<{ href: string; label: string; onClick?: () => void }> = ({
  href,
  label,
  onClick,
}) => (
  <NavLink
    to={href}
    onClick={onClick}
    className={({ isActive }) =>
      `relative text-base sm:text-[15px] min-[820px]:text-sm min-[900px]:text-[15px] lg:text-sm xl:text-base font-medium transition-all duration-300 cursor-pointer pb-1 group ${
        isActive
          ? "dark:text-white text-blue-700"
          : "dark:text-gray-300 text-gray-600 dark:hover:text-white hover:text-blue-700"
      }`
    }
  >
    {({ isActive }) => (
      <>
        {label}
        {/* Underline — needs w-full to have a measurable width to scale */}
        <span
          className={`absolute -bottom-1 left-0 w-full h-0.5 bg-blue-600 origin-left transform transition-transform duration-300 ${
            isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
          }`}
        />
      </>
    )}
  </NavLink>
);

export const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();

  // ── Scroll detection — compact nav after 40px ─────────────────
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // run once on mount
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openMobileMenu = () => {
    setMobileOpen(true);
    requestAnimationFrame(() => setMenuVisible(true));
  };

  const closeMobileMenu = () => {
    setMenuVisible(false);
    setTimeout(() => setMobileOpen(false), 280);
  };

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => e.key === "Escape" && closeMobileMenu();
    // Align with Tailwind min-[820px] — inline nav for iPad Air / most iPad portrait widths
    const handleResize = () => window.innerWidth >= 820 && closeMobileMenu();

    if (mobileOpen) {
      document.addEventListener("keydown", handleEscape);
      window.addEventListener("resize", handleResize);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = "unset";
    };
  }, [mobileOpen]);

  return (
    <header
      className={`sticky inset-x-0 top-0 z-50 flex w-full max-w-[100vw] flex-col self-start [backface-visibility:hidden] [transform:translateZ(0)] transition-all duration-300 ease-in-out
        dark:border-b dark:border-white/[0.06] border-b border-gray-200/80
        dark:bg-[#020D30]/85 bg-white/90 backdrop-blur-xl
        ${scrolled ? "dark:bg-[#020D30]/95 bg-white/95" : ""}`}
    >
      {/* iPad / notched devices: reserve status area without breaking fixed positioning */}
      <div
        className="pointer-events-none w-full shrink-0 [height:env(safe-area-inset-top,0px)]"
        aria-hidden
      />
      <div
        className={`flex min-h-0 w-full flex-nowrap items-center justify-between gap-1 sm:gap-2
          pl-[max(0.5rem,env(safe-area-inset-left,0px))] pr-[max(0.5rem,env(safe-area-inset-right,0px))]
          sm:pl-[max(1.25rem,env(safe-area-inset-left,0px))] sm:pr-[max(1.25rem,env(safe-area-inset-right,0px))]
          md:px-10 lg:px-14 xl:px-20
          ${scrolled
            ? "h-12 sm:h-14 md:h-[60px] lg:h-16 xl:h-[68px]"
            : "h-14 sm:h-16 md:h-[72px] lg:h-20 xl:h-[84px]"
          }`}
      >
      {/* Logo — tighter on narrow widths so theme + menu always fit */}
      <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-3 min-[820px]:flex-none">
        <NavLink to="/" className="min-w-0 max-w-[min(100%,calc(100vw-5.75rem))] shrink min-[820px]:max-w-none">
          <img
            src="/assets/logo/vawena-logo.png"
            alt="VaWeNa Logo"
            width={196}
            height={40}
            className={`h-auto hidden dark:block transition-all motion-reduce:transition-none duration-300 max-w-full ${
              scrolled
                ? "w-[72px] sm:w-[108px] md:w-[122px] lg:w-[136px] xl:w-[148px]"
                : "w-[80px] sm:w-[128px] md:w-[152px] lg:w-[176px] xl:w-[196px]"
            }`}
          />
          <img
            src="/assets/logo/vawena-logo-light.png"
            alt="VaWeNa Logo"
            width={196}
            height={40}
            className={`h-auto block dark:hidden transition-all motion-reduce:transition-none duration-300 max-w-full ${
              scrolled
                ? "w-[72px] sm:w-[108px] md:w-[122px] lg:w-[136px] xl:w-[148px]"
                : "w-[80px] sm:w-[128px] md:w-[152px] lg:w-[176px] xl:w-[196px]"
            }`}
            onError={(e) => { (e.target as HTMLImageElement).src = '/assets/logo/vawena-logo.png'; }}
          />
        </NavLink>
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden min-[820px]:flex min-[820px]:gap-3 lg:gap-6 xl:gap-8 2xl:gap-11 items-center shrink-0">
        {navLinks.map((link) => (
          <NavItem key={link.href} href={link.href} label={link.label} />
        ))}
      </nav>

      {/* Desktop Buttons */}
      <div className="hidden min-[820px]:flex min-[820px]:gap-1.5 lg:gap-2 xl:gap-3 2xl:gap-4 items-center shrink-0">
        <ThemeToggle />
        {location.pathname !== "/login" && (
          <SleekButton
            variant="light"
            className="!min-w-0 !px-2.5 !py-2 !text-[11px] min-[900px]:!px-3 min-[900px]:!text-xs lg:!px-3.5 lg:!py-2.5 lg:!text-sm xl:!px-5 xl:!py-3 xl:!text-sm"
            onClick={() => window.open("https://app.vawena.ai/login", "_self")}
          >
            Log In
          </SleekButton>
        )}
        <SleekButton
          variant="primary"
          className="!min-w-0 !px-2.5 !py-2 !text-[11px] min-[900px]:!px-3 min-[900px]:!text-xs lg:!px-3.5 lg:!py-2.5 lg:!text-sm xl:!px-5 xl:!py-3 xl:!text-sm"
          onClick={() => window.open("https://app.vawena.ai/signup", "_self")}
        >
          Get Started
        </SleekButton>
      </div>

      {/* Mobile Menu Toggle — fixed icon box so hamburger/X never clip */}
      <div className="flex min-[820px]:hidden shrink-0 items-center gap-0 sm:gap-2">
        <ThemeToggle />
        <button
          type="button"
          onClick={() => mobileOpen ? closeMobileMenu() : openMobileMenu()}
          className="flex size-9 touch-manipulation items-center justify-center rounded-md transition-colors duration-300 sm:size-10 dark:hover:bg-white/10 hover:bg-gray-100"
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          <span className="relative flex size-6 items-center justify-center" aria-hidden>
            <FiMenu
              strokeWidth={2.25}
              className={`absolute size-6 text-gray-800 transition-all duration-300 dark:text-white ${
                mobileOpen ? "rotate-90 opacity-0 scale-50" : "rotate-0 opacity-100 scale-100"
              }`}
            />
            <FiX
              strokeWidth={2.25}
              className={`absolute size-6 text-gray-800 transition-all duration-300 dark:text-white ${
                mobileOpen ? "rotate-0 opacity-100 scale-100" : "rotate-90 opacity-0 scale-50"
              }`}
            />
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ease-out ${
              menuVisible ? "opacity-100" : "opacity-0"
            }`}
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            className={`absolute left-0 top-full z-50 w-full border-t border-gray-200 bg-white shadow-xl transition-all duration-300 ease-out dark:border-gray-700 dark:bg-[#000F38] ${
              menuVisible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
            }`}
          >
            <div className="flex max-h-[min(85dvh,calc(100dvh_-_3.5rem))] flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 sm:py-6 md:px-10 md:py-7">
              <nav className="flex flex-col items-center gap-4 sm:gap-5 md:gap-6" aria-label="Mobile">
                {navLinks.map((link) => (
                  <NavItem
                    key={link.href}
                    href={link.href}
                    label={link.label}
                    onClick={closeMobileMenu}
                  />
                ))}
              </nav>

              <div className="mt-1 flex w-full flex-col gap-3 pb-[max(1rem,env(safe-area-inset-bottom,0px))] sm:mx-auto sm:max-w-sm">
                {location.pathname !== "/login" && (
                  <SleekButton
                    variant="light"
                    className="w-full justify-center"
                    onClick={() => { closeMobileMenu(); window.open("https://app.vawena.ai/login", "_self"); }}
                  >
                    Log In
                  </SleekButton>
                )}
                <SleekButton
                  variant="primary"
                  className="w-full justify-center"
                  onClick={() => { closeMobileMenu(); window.open("https://app.vawena.ai/signup", "_self"); }}
                >
                  Get Started
                </SleekButton>
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </header>
  );
};
